window.ajaxUtils = (function (cu, reactivity) {
	const DEF_EVENT_NAME = 'onload';
	const META_HOME = '_home';
	const META_CURRENT = '_current';
	const META_PERSISTENT_ARGS = '_parms';
	const FORMAT_PREFIX = '__format_';

	const { reactive, computed, effect, effectScope, isReactive } = reactivity;

	var timer = null;
	var intervals = {};

	var state = {
		com: {
			request: { default: null },
			message: null,
			apiResult: null,
			requestedJs: []
		},
		loc: {
			url: null,
			parms: {},
			onBack: null,
			storage: [{}],
			clientCtx: {}
		},
		ctrl: {}
	};

	// ==================== Microtask Batching ====================

	var _pendingEffects = new Set();
	var _isFlushing = false;

	// Creates an effect that batches DOM updates via microtask queue.
	// When lazy is true, the effect is created but NOT scheduled automatically.
	// The caller is responsible for collecting the returned runner and flushing
	// it at the right time (e.g. after state has been fully populated).
	function createBatchedEffect(fn, scope, lazy) {
		var runner;
		var scheduleFn = function () {
			if (typeof runner === 'function') {
				_pendingEffects.add(runner);
			} else if (runner && typeof runner.run === 'function') {
				// @vue/reactivity may pass a ReactiveEffect object
				_pendingEffects.add(function () { runner.run(); });
			}
			if (!_isFlushing) {
				_isFlushing = true;
				Promise.resolve().then(function () {
					_isFlushing = false;
					var effects = Array.from(_pendingEffects);
					_pendingEffects.clear();
					for (var i = 0; i < effects.length; i++) {
						effects[i]();
					}
				});
			}
		};
		if (scope) {
			runner = scope.run(function () {
				return effect(fn, { scheduler: scheduleFn, lazy: !!lazy });
			});
		} else {
			runner = effect(fn, { scheduler: scheduleFn, lazy: !!lazy });
		}
		if (!lazy) {
			// Non-lazy effects are queued immediately (existing behavior)
		}
		return runner;
	}

	// ==================== End Microtask Batching ====================

	var instance = {
		initForm: function (args) {
			var form = document.getElementById(args.id);
			if (!form) return;

			// Delegated click handler for submit buttons
			form.addEventListener('click', function (event) {
				const target = event.target;
				if (target.matches('input[type="submit"], button[type="submit"]')) {
					if (target.getAttribute('data-cancelevent') == 'true') return;
					/* horrible hack to detect form submissions via ajax */
					event.preventDefault();
					// Trigger submit event with submitter info
					const submitEvent = new CustomEvent('submit', {
						bubbles: true,
						cancelable: true,
						detail: { submitter: target }
					});
					target.form.dispatchEvent(submitEvent);
				}
			});

			// Delegated keydown handler for text inputs
			form.addEventListener('keydown', function (event) {
				if (event.target.matches('input[type="text"]')) {
					/* horrible hack to detect form submissions via ajax */
					if (event.target.readOnly)
						event.preventDefault();
				}
			});

			// Form submit handler
			form.addEventListener('submit', function (e) {
				// Get submitter from CustomEvent detail or use first submit button
				const submitter = e.submitter || (e.detail && e.detail.submitter) || form.querySelector('[type="submit"]');

				if (submitter && submitter.classList.contains('ajax-loading'))
					return false;

				if (submitter) {
					const confirmMsg = submitter.getAttribute('data-confirm');
					if (confirmMsg) {
						if (!window.confirm(confirmMsg)) {
							e.preventDefault();
							return false;
						}
					}
				}

				e.preventDefault();
				return instance.formSubmit(submitter, form);
			});

			if (!args.submitOnEnter) {
				form.addEventListener('keypress', function (e) {
					if (e.target.matches(':not(textarea):not([type=submit])') &&
						(e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA')) {
						if (e.keyCode === 13) {
							e.preventDefault();
							return false;
						}
					}
				});
			}
		},
		formSubmit: function (sender, form) {
			if (form.hasAttribute('data-res') && instance.processResult(form) == false) return false;
			var fd = new FormData(form);
			fd.append('submit', sender.value);
			var els = form.elements;
			for (var i = 0, el; el = els[i++];) {
				processElementDataOnFormSubmit(el, function (key, value) { fd.append(key, value); });
			}
			var target = { data: fd, method: 'POST' };
			processElementDataOnEvent(sender, target, function (key, value) { fd.append(key, value); });
			if (!target.e) target.e = 'onsubmit';

			runOnAjaxSend(sender, target);
			const r = instance.postEventWithApiResponse(target);
			if (form.hasAttribute('data-res-postponed'))
				r.then(function (apiResult) {
					if (apiResult.success != false)
						instance.processResult(form);
				});

			return false;
		},
		delay: function (caller, func, timeout) {
			if (!timeout) {
				timeout = 400;
			}
			if (timer) {
				window.clearTimeout(timer);
				timer = null;
			}
			timer = window.setTimeout(function () { func(caller); }, timeout);
		},
		bindevent: function (args) {
			var el = document.getElementById(args.id);
			if (!el) return;
			el.addEventListener(args.clientEvent, function (e) {
				if (args.method && args.method == 'get')
					instance.runEventFromElementWithApiResponse(this, { e: args.serverEvent, r: args.serverEventReceiver });
				else
					instance.postEventFromElementWithApiResponse(this, { e: args.serverEvent, r: args.serverEventReceiver });
			});
		},
		repeatedPostEvent: function (args) {
			if (intervals[args.id]) return;

			var n = window.setInterval(function () {
				var el = document.getElementById(args.id);
				if (el)
					instance.postEventFromElementWithApiResponse(el);
				else if (intervals[args.id]) {
					window.clearInterval(intervals[args.id]);
					delete intervals[args.id];
				}
			}, args.interval);

			intervals[args.id] = n;
		},
		clearRepeatedPostEvent: function (id) {
			if (intervals[id]) {
				window.clearInterval(intervals[id]);
				delete intervals[id];
			}
		},
		runEvent: function (target) {
			const settings = {
				url: instance.prepareUrl(target),
				type: 'GET',
				responseType: target.responseType ? target.responseType : "",
				requestGroup: target.requestGroup ? target.requestGroup : null,
				sender: target.sender
			};
			return ajax(settings).catch(function (err) {
				Tango.processError(err.xhr, err.settings, requestCompleted);
				return Promise.reject(err);
			}).then(onRequestResult);
		},
		runEventWithApiResponse: function (target) {
			if (target.method == 'FAKEGET')
				return instance.postEvent(target).then(processApiResponse);
			else
				return instance.runEvent(target).then(processApiResponse);
		},
		runEventFromElementWithApiResponse: function (el, target) {
			if (el.hasAttribute('data-res') && instance.processResult(el) == false) return;
			if (!target) target = {};
			if (!target.data) target.data = {};
			if (!target.query) target.query = {};
			if (!target.method) target.method = 'FAKEGET';
			processElementDataOnEvent(el, target, function (key, value) { target.data[key] = value; });
			if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement || el.hasAttribute('data-ctrl')) {
				cu.processElementValue(el, function (key, value) { target.query[key] = value; })
				//target.query[el.name] = el.value;
			}
			runOnAjaxSend(el, target);
			return instance.runEventWithApiResponse(target);
		},
		postEvent: function (target) {
			const isForm = target.data instanceof FormData;
			const settings = {
				url: instance.prepareUrl(target),
				type: 'POST',
				processData: !isForm,
				contentType: isForm ? false : "application/json; charset=utf-8",
				data: isForm ? target.data : JSON.stringify(target.data),
				requestGroup: target.requestGroup ? target.requestGroup : null,
				responseType: target.responseType,
				sender: target.sender,
				headers: {}
			};
			if (target.method == 'FAKEGET')
				settings.headers['X-HTTP-Method'] = 'GET';
			return ajax(settings).catch(function (err) {
				Tango.processError(err.xhr, err.settings, requestCompleted);
				return Promise.reject(err);
			}).then(onRequestResult);
		},
		postEventWithApiResponse: function (target) {
			return instance.postEvent(target).then(processApiResponse);
		},
		postEventFromElementWithApiResponse: function (el, target) {
			if (el.hasAttribute('data-res') && instance.processResult(el) == false) return;
			const form = el.closest('form');
			if (!target) target = {};
			if (!target.data) target.data = {};
			if (!target.query) target.query = {};
			if (form) {
				target.data = Tango.serializeFormToObject(form);
				var els = form.elements;
				for (var i = 0, fel; fel = els[i++];) {
					processElementDataOnFormSubmit(fel, function (key, value) { target.data[key] = value; });
				}
			}

			target.method = 'POST';
			processElementDataOnEvent(el, target, function (key, value) { target.data[key] = value; });
			if (!form && (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement || el.hasAttribute('data-ctrl'))) {
				cu.processElementValue(el, function (key, value) { target.data[key] = value; })
				//target.data[el.name] = el.value;
			}

			runOnAjaxSend(el, target);
			const r = instance.postEventWithApiResponse(target);
			if (el.hasAttribute('data-res-postponed'))
				return r.then(function (apiResult) {
					if (apiResult.success != false)
						instance.processResult(el);
				});
			else
				return r;
		},
		runHrefWithApiResponse: function (a, target) {
			if (event && event.ctrlKey && a && a.href) {
				open(a.href, '_blank').focus();
				return;
			}
			if (!target) target = {};
			target.changeloc = true;
			instance.runEventFromElementWithApiResponse(a, target);
		},
		prepareUrl: function (target) {
			var parms = {};
			state.loc.next = {};

			if (!target.currenturl)
				target.currenturl = target.url;

			var argGroups = [], parms2remove = [];
			if (target.sender) {
				const grAttr = target.sender.getAttribute('data-cleararggroup');
				if (grAttr)
					argGroups = grAttr.split(',');
			}

			if (argGroups.length > 0 && state.loc.arggroups) {
				for (var i = 0; i < argGroups.length; i++) {
					if (state.loc.arggroups.hasOwnProperty(argGroups[i])) {
						parms2remove = parms2remove.concat(state.loc.arggroups[argGroups[i]]);
					}
				}
			}

			var sep = target.url.indexOf('?');
			var targetpath = sep >= 0 ? target.url.substring(0, sep) : target.url;
			const targetquery = sep >= 0 ? target.url.substring(sep + 1) : '';
			const targetqueryparms = cu.getParams(targetquery, true);
			for (var key in target.query) {
				targetqueryparms[key] = encodeURIComponent(target.query[key]);
			}
			for (var key in target.data) {
				if (targetqueryparms[key]) {
					targetqueryparms[key] = encodeURIComponent(target.data[key]);
					delete target.data[key];
				}
			}
			target.url = targetpath + '?';
			for (var key in targetqueryparms) {
				if (targetqueryparms[key] && targetqueryparms[key] != '' && !parms2remove.includes(key))
					target.url += key + '=' + targetqueryparms[key] + '&';
			}
			target.url = target.url.slice(0, -1);

			if (target.changeloc) {
				state.loc.next.url = target.url;
			}

			if (target.method == 'GET') {
				for (var key in target.data) {
					parms[key] = target.data[key];
				}
			}

			var page = document.head.getAttribute('data-page');
			if (page) parms.p = page;
			if (target.r) parms.r = target.r;
			if (target.sender && target.sender.id) parms.sender = target.sender.id;
			parms.e = target.e ? target.e : DEF_EVENT_NAME;

			state.loc.next.parms = parms;

			var curpath = target.currenturl;
			sep = curpath.indexOf('?');
			curpath = sep >= 0 ? curpath.substring(0, sep) : curpath;

			if (curpath == '/' || targetpath == '/') {
				const home = document.getElementById(META_HOME);
				const alias = home.getAttribute('data-alias');
				if (curpath == '/') curpath = alias || '/';
				if (targetpath == '/') targetpath = alias || '/';
			}
			var base = document.getElementsByTagName('base')[0];
			base = base ? base.getAttribute('href') : '';
			if (base && !targetpath.startsWith(base))
				targetpath = base + targetpath;
			if (base && !curpath.startsWith(base))
				curpath = base + curpath;

			const result = target.sender ?
				target.sender.getAttribute('data-res') || target.sender.getAttribute('data-res-postponed') : undefined;

			if (result == 1 && state.loc.storage.length > 1) {
				const returnstate = JSON.stringify(state.loc.storage[state.loc.storage.length - 2]);
				if (target.data instanceof FormData)
					target.data.append('returnstate', returnstate);
				else
					target.data['returnstate'] = returnstate;
			}

			state.loc.next.arggroups = state.loc.arggroups;
			state.loc.next.storage = state.loc.storage.slice();

			if (targetpath.toLowerCase() != curpath.toLowerCase()) {
				parms['c-new'] = 1;
				state.loc.next.arggroups = undefined;
				if (target.changeloc) {
					state.ctrl = {};
					if (result == 0) {
						state.loc.next.storage.pop();
						if (state.loc.next.storage.length == 0)
							state.loc.next.storage.push({});
						const curStorage = state.loc.next.storage[state.loc.next.storage.length - 1];
						for (var key in curStorage) {
							target.data[key] = curStorage[key];
						}
					}
				}

				if (!result && (target.changeloc || target.changeloc_modal)) {
					state.loc.next.storage.push({});
				}
			}
			else if (!parms['c-prefix'] && target.containerPrefix) {
				parms['c-prefix'] = target.containerPrefix;
				parms['c-type'] = target.containerType;
			}

			if (target.responseType)
				parms['responsetype'] = target.responseType;

			state.loc.next.changeloc = target.changeloc;
			if (target.changeloc) {
				if (target.onBack) state.loc.next.onBack = target.onBack;
				//window.history.pushState(state.loc, "", target.url);
			}

			for (var key in parms) {
				if (!parms[key]) delete parms[key];
			}

			target.parms = parms;

			return getApiUrl(target.url, target.parms, target.isfirstload);
		},
		processResult: function (el) {
			const result = el.getAttribute('data-res') || el.getAttribute('data-res-postponed');
			const handler = commonUtils.getThisOrParent(el, function (parent) { return parent.hasAttribute('data-res-handler'); });

			if (!handler) return;

			const callOnResult = function (ctrl) {
				const t = ctrl.getAttribute('data-ctrl');
				const ctrlid = ctrl.hasAttribute('data-ctrl-id') ? ctrl.getAttribute('data-ctrl-id') : ctrl.id;
				const inst = state.ctrl[ctrlid];

				if (inst && inst.onResult)
					return inst.onResult(result);
				else if (window[t] && window[t]['onResult']) {
					return window[t]['onResult'](result, inst ?? { root: ctrlid, type: t });
				}
			};

			// обработка закрытия модальных окон по кнопке "Назад"
			// TODO: переделать в будущем с использованием идентификатора узла навигации
			const href = handler.getAttribute('data-href');
			if (href && (new URL(location.origin + href)).pathname != location.pathname) {
				state.loc.storage.pop();
			}

			const children = handler.querySelectorAll('[data-ctrl]');

			for (var i = 0; i < children.length; i++) {
				if (callOnResult(children[i]) == false) {
					return false;
				}
			}

			if (callOnResult(handler) == false) {
				return false;
			}
		},
		findServiceAction: function (el) {
			var root = el;
			if (root != document.head) {
				root = cu.getParent(el, function (n) { return n.hasAttribute && n.hasAttribute('data-href'); });
				if (!root) root = document.getElementById(META_CURRENT);
			}
			const home = document.getElementById(META_HOME);
			return root.getAttribute('data-href') || home.getAttribute('data-href') || '/';
		},
		findControl: function (el) {
			const ctrl = cu.getThisOrParent(el, function (n) { return n.hasAttribute && n.hasAttribute('data-ctrl'); });
			if (!ctrl) return null;
			const id = ctrl.hasAttribute('data-ctrl-id') ? ctrl.getAttribute('data-ctrl-id') : ctrl.id;
			return { root: ctrl, id: id, instance: state.ctrl[id] };
		},
		setValue: function (args) {
			var e = document.getElementById(args.id);
			if (e) {
				if (e instanceof HTMLInputElement || e instanceof HTMLSelectElement)
					e.value = args.value;
				else
					e.innerHTML = args.value;
				const st = getElementStateAttrs(e);
				if (st) {
					if (st.type == 'array') {
						const values = args.value.split(',').filter(String);
						const arr = state.ctrl[st.owner][st.name];
						// arr is a reactive array — splice/push trigger effects automatically
						arr.splice(0, arr.length);
						for (var i = 0; i < values.length; i++) {
							arr.push(values[i]);
						}
					}
				}
			}
		},
		changeUrl: function (args) {
			const params = location.search.slice(1).split('&');
			var search = [];
			var hash = location.hash == '' ? [] : location.hash.split('&');
			var hstate = getHistoryState();

			if (args.remove && args.remove.length > 0) {
				const deleteRegex = new RegExp(args.remove.join('=|') + '=');
				for (var i = 0; i < params.length; i++)
					if (deleteRegex.test(params[i]) === false)
						search.push(params[i]);
				if (hstate) {
					for (var key in args.remove) {
						if (hstate.parms[key]) delete hstate.parms[key];
					}
				}
			}
			else
				for (var i = 0; i < params.length; i++)
					search.push(params[i]);

			if (args.add) {
				for (var key in args.add) {
					if (key.startsWith('#'))
						hash.push(key.substring(1) + (args.add[key] != '' ? '=' + args.add[key] : ''));
					else {
						search.push(key + '=' + args.add[key]);
						if (hstate) hstate.parms[key] = args.add[key];
					}
				}
			}
			var url = location.pathname + (search.length ? '?' + search.join('&') : '') + (hash.length > 0 ? '#' + hash.join('&') : '');

			if (hash.length > 0)
				window.location.hash = hash.join('&');

			if (hstate) window.history.replaceState(hstate, document.title, url);
			state.loc.url = url;

			const current = document.getElementById(META_CURRENT);
			current.setAttribute('data-href', url);
		},
		state: state,
		processControls: processControls,
		postProcessControls: postProcessControls,
		processApiResponse: function (apiResult) {
			processApiResponse(apiResult);
		}
	};

	function getApiUrl(path, parms, isfirstload) {
		const k = path.indexOf('?');
		if (!parms) parms = {};

		var url;
		if (k > 0) {
			url = path.substring(0, k);
			const urlParms = cu.getParams(path.substring(k + 1));
			for (var key in urlParms) {
				if (!parms[key]) {
					parms[key] = urlParms[key];
				}
			}
		}
		else
			url = path;

		url += '?';

		for (var key in parms) {
			url += key + '=' + encodeURIComponent(parms[key]) + '&';
		}

		return isfirstload ? url += 'firstload=true' : url.slice(0, -1);
	}

	function ajax(settings) {
		const isArrayBuffer = settings.responseType && settings.responseType.toLowerCase() == 'arraybuffer';

		return new Promise(function (resolve, reject) {
			const xhr = new XMLHttpRequest();
			xhr.open(settings.type, settings.url);
			if (isArrayBuffer) xhr.responseType = 'arraybuffer';

			xhr.onload = function () {
				if (this.status >= 200 && this.status < 300) {
					resolve({
						data: isArrayBuffer ? xhr.response : JSON.parse(xhr.response),
						xhr: xhr,
						settings: settings
					});
				} else {
					reject({ xhr: xhr, settings: settings });
				}
			};

			xhr.onerror = function () {
				reject({ xhr: xhr, settings: settings });
			};

			xhr.contentType = settings.contentType;
			xhr.processData = settings.processData;
			xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
			if (settings.headers)
				for (var h in settings.headers)
					xhr.setRequestHeader(h, settings.headers[h]);
			beforeRequest(xhr, settings);
			xhr.send(settings.data);
		});
	}

	function beforeRequest(xhr, settings) {
		const reqId = cu.createGuid();
		const reqGroup = settings.requestGroup ? settings.requestGroup : 'default';
		state.com.request[reqGroup] = reqId;
		xhr.setRequestHeader('x-request-guid', reqId);
		xhr.setRequestHeader('x-csrf-token', document.head.getAttribute('data-x-csrf-token'));
		setTimeout(function () {
			if (state.com.request.default && state.com.message) document.body.style.cursor = 'wait';
		}, 100);
		setTimeout(function () {
			if (state.com.request.default && state.com.message) {
				state.com.message.style.display = 'block';
			}
		}, 1000);
	}

	function requestCompleted(xhr, settings) {
		const reqGroup = settings.requestGroup ? settings.requestGroup : 'default';
		state.com.request[reqGroup] = null;
		if (document.body) document.body.style.cursor = '';
		if (state.com.message) {
			state.com.message.style.display = 'none';
		}

		const nodes = document.querySelectorAll('.ajax-loading');

		for (var i = 0; i < nodes.length; i++) {
			nodes[i].classList.remove('ajax-loading');
		}
	}

	function onRequestResult(result) {
		const data = result.data;
		const xhr = result.xhr;
		const settings = result.settings;

		const reqGroup = settings.requestGroup ? settings.requestGroup : 'default';
		if (xhr.getResponseHeader('X-Request-Guid') == state.com.request[reqGroup]) {
			requestCompleted(xhr, settings);
			const disposition = xhr.getResponseHeader('Content-Disposition');

			// check file download response
			if (disposition && disposition.indexOf('attachment') !== -1) {
				const contenttype = xhr.getResponseHeader('Content-Type');
				cu.processFile(contenttype, disposition, data);
				if (settings.sender) {
					settings.sender.dispatchEvent(new CustomEvent('downloadComplete'));
					if (settings.sender.hasAttribute('data-callbackelement')) {
						const callbackel = document.getElementById(settings.sender.getAttribute('data-callbackelement'));
						if (callbackel)
							instance.postEventFromElementWithApiResponse(callbackel);
					}
				}
				//alert('file downloaded');
				return Promise.reject({ cancelled: true });
			}
			else
				return Promise.resolve(data);
		}
		else
			return Promise.reject({ cancelled: true });
	}

	function processElementDataOnEvent(el, target, setvalfunc) {
		for (var attr, i = 0, attrs = el.attributes, n = attrs ? attrs.length : 0; i < n; i++) {
			attr = attrs[i];
			var val = attr.value == '' ? null : attr.value;
			if (attr.name.startsWith('data-p-')) {
				if (target.method == 'POST') {
					if (target.data instanceof FormData) {
						target.data.append(attr.name.replace('data-p-', ''), val || '');
					}
					else
						target.data[attr.name.replace('data-p-', '')] = val || '';
				}
				else
					target.query[attr.name.replace('data-p-', '')] = val || '';
			} else if (attr.name == 'href') {
				target.url = val;
			} else if (attr.name == 'data-href') {
				target.url = val;
			} else if (attr.name == 'data-e') {
				target.e = val;
			} else if (attr.name == 'data-r') {
				target.r = val;
			} else if (attr.name.startsWith('data-format')) {
				target.data[FORMAT_PREFIX + el.name] = val;
			} else if (attr.name.startsWith('data-c-')) {
				target.data[attr.name.replace('data-c-', 'c-')] = val || '';
			} else if (attr.name == 'data-ref') {
				const ids = val.split(' ');
				ids.forEach(id => cu.processElementValue(document.getElementById(id), setvalfunc));
			} else if (attr.name == 'data-ref-sessionstorage') {
				const ids = val.split(' ');
				ids.forEach(id => setvalfunc(id, sessionStorage.getItem(id)));
			} else if (attr.name == 'data-responsetype') {
				target.responseType = val;
			}
		}

		if (el.hasAttribute('data-requestgroup'))
			target.requestGroup = el.getAttribute('data-requestgroup');

		target.currenturl = instance.findServiceAction(el);
		if (!target.url) {
			target.url = target.currenturl;
		}

		target.sender = el;

		const startEl = el.hasAttribute('data-c-external') ? document.getElementById(el.getAttribute('data-c-external')) : el;
		const isDataRes = el.hasAttribute('data-res') || el.hasAttribute('data-res-postponed');
		const applyClientCtx = el.getAttribute('data-applyclientctx');

		var firstContainer, noChangeLoc;
		cu.getThisOrParent(startEl, function (n) {
			if (!firstContainer && n.hasAttribute && n.hasAttribute('data-c-prefix'))
				firstContainer = n;
			if (!noChangeLoc && n.hasAttribute && n.getAttribute('data-c-changeloc') == 'false')
				noChangeLoc = n;
		});

		if (firstContainer) {
			target.containerPrefix = firstContainer.getAttribute('data-c-prefix');
			target.containerType = firstContainer.getAttribute('data-c-type');
			if (applyClientCtx) {
				const ctxArgs = state.loc.clientCtx[applyClientCtx];
				for (var key in ctxArgs) {
					if (target.data[key] == null) {
						target.data[key] = ctxArgs[key];
					}
				}
			}

			if (!isDataRes && noChangeLoc)
				target.changeloc = false;
		}

		// TODO: доработать
		// - перенести обработку backArgs в prepareTarget
		if (el.hasAttribute('data-c-new') && el.hasAttribute('data-c-type')) {
			const t = el.getAttribute('data-c-type').toLowerCase();
			const clientCtxName = el.getAttribute('data-p-c-clientctx');

			if (t.startsWith('dialog')) {
				target.changeloc_modal = true;
			}

			if (clientCtxName) {
				var ctxArgs = {};
				for (var key in target.data) {
					if (!key.startsWith('c-'))
						ctxArgs[key] = target.data[key];
				}
				if (!state.loc.clientCtx) state.loc.clientCtx = {};
				state.loc.clientCtx[clientCtxName] = ctxArgs;
			}
		}
	}

	function processElementDataOnFormSubmit(el, setvalfunc) {
		for (var attr, i = 0, attrs = el.attributes, n = attrs ? attrs.length : 0; i < n; i++) {
			attr = attrs[i];
			if (attr.name.startsWith('data-format')) {
				setvalfunc(FORMAT_PREFIX + el.name, attr.value);
			}
		}
	}

	function processApiResponse(apiResult) {
		console.log('processApiResponse');

		if (!apiResult) return;
		if (apiResult instanceof ArrayBuffer) {
			const s = new TextDecoder('utf-8').decode(apiResult);
			apiResult = JSON.parse(s);
		}

		if (apiResult.url) {
			window.location = apiResult.url;
			return;
		}

		state.com.apiResult = apiResult;

		if (document.readyState == 'complete' || document.readyState == 'interactive') {
			try {
				renderApiResult();
			} catch (ex) {
				Tango.showError(localization.resources.title.javascriptError, ex + '\n\n' + ex.stack, 'err');
			}
		}

		return Promise.resolve(apiResult);
	}

	function getElementStateAttrs(el) {
		const type = el.getAttribute('data-hasclientstate');
		var owner = el.getAttribute('data-clientstate-owner');
		const name = el.getAttribute('data-clientstate-name') || el.name;
		if (!owner) {
			const parentctrl = instance.findControl(el);
			if (!parentctrl) return null;
			owner = parentctrl.id;
		}
		const bindtype = el.getAttribute('data-clientstate-bindtype') || 'oneway';
		const result = { type: type, owner: owner, name: name, bindtype: bindtype };

		// arrayitem: mapping to a specific element of an array
		if (type == 'arrayitem') {
			const index = el.getAttribute('data-clientstate-index');
			const key = el.getAttribute('data-clientstate-key');
			const keyfield = el.getAttribute('data-clientstate-keyfield') || 'id';
			const field = el.getAttribute('data-clientstate-field');
			if (field != null) result.field = field;
			if (index != null) result.index = parseInt(index, 10);
			if (key != null) {
				result.key = key;
				result.keyfield = keyfield;
			}
		}

		return result;
	}

	function renderApiResult() {
		var apiResult = state.com.apiResult;
		state.com.apiResult = null;

		if (apiResult.error) {
			Tango.showError(localization.resources.title.systemError, apiResult.error, 'err');
			return;
		}

		if (state.loc.next) {
			if (state.loc.next.hasOwnProperty('url')) state.loc.url = state.loc.next.url;
			if (state.loc.next.hasOwnProperty('parms')) state.loc.parms = state.loc.next.parms;
			if (state.loc.next.hasOwnProperty('arggroups')) state.loc.arggroups = state.loc.next.arggroups;
			if (state.loc.next.hasOwnProperty('storage')) state.loc.storage = state.loc.next.storage;
			if (state.loc.next.hasOwnProperty('onBack')) state.loc.onBack = state.loc.next.onBack;
			if (state.loc.next.hasOwnProperty('onBackArgs')) state.loc.onBackArgs = state.loc.next.onBackArgs;

			if (state.loc.next.changeloc && !apiResult.redirect && !apiResult.hardredirect && !state.com.onPopState) {
				window.history.pushState(state.loc, "", state.loc.url);
				console.log('push state:', state.loc.url);
			}
			state.loc.next = undefined;
			if (state.com.onPopState)
				delete state.com.onPopState;
		}

		const nodes = [];
		const shadow = (new DOMParser()).parseFromString("<!DOCTYPE html>", "text/html");

		const replaceid = function (el, obj) {
			if (!obj.content.firstChild) return;
			if (obj.name == el.id)
				obj.content.firstChild.id = el.id;
			else if (obj.content.firstChild.id == '' && obj.content.querySelector("[id='" + el.id + "']") == null)
				obj.content.firstChild.id = el.id;
		};

		const replaceFunc = function (el, obj) {
			//if (obj.content.childNodes.length > 1)
			//	throw 'Replace widget: the replacement content must contain only one node.';
			//else
			if (obj.content.childNodes.length > 0) {
				replaceid(el, obj);
				if (obj.selector) {
					if (el && document.body.contains(el))
						el.parentNode.replaceChild(obj.content.firstChild, el);
					else
						adjacentFunc(null, obj);
				}
				else
					el.parentNode.replaceChild(obj.content.firstChild, el);
			}
		};
		const addFunc = function (el, obj) {
			if (el.parentNode && obj.content.childNodes.length == 1 && el.id == obj.content.firstChild.id) {
				el.parentNode.replaceChild(obj.content.firstChild, el);
				return;
			}
			while (el.firstChild) {
				el.removeChild(el.firstChild);
			}
			while (obj.content.childNodes.length > 0)
				el.appendChild(obj.content.firstChild);
		};
		const addShadowFunc = function (el, obj) {
			const shadowRoot = el.attachShadow({ mode: 'open' });
			shadowRoot.innerHTML = obj.content;

			while (shadowRoot.firstChild) {
				shadowRoot.removeChild(shadowRoot.firstChild);
			}
			while (obj.content.childNodes.length > 0)
				shadowRoot.appendChild(obj.content.firstChild);
		};
		const adjacentFunc = function (el, obj) {
			if (obj.selector) {
				el = document.querySelector(obj.parent.substring(1));
				if (!el) return;
			}
			while (obj.content.childNodes.length > 0) {
				const i = obj.position == 'beforeEnd' || obj.position == 'beforeBegin' ? 0 : obj.content.childNodes.length - 1;
				el.insertAdjacentElement(obj.position, obj.content.childNodes[i]);
			}
			if (obj.position == 'afterEnd') {
				cu.scrollToView(el.nextSibling);
			}
		};

		var rtagName = /<([\w:]+)/,
			// We have to close these tags to support XHTML (#13200)
			wrapMap = {
				option: [1, "<select multiple='multiple'>", "</select>"],
				thead: [1, "<table>", "</table>"],
				col: [2, "<table><colgroup>", "</colgroup></table>"],
				tr: [2, "<table><tbody>", "</tbody></table>"],
				td: [3, "<table><tbody><tr>", "</tr></tbody></table>"],
				_default: [0, "", ""]
			};

		function parseHTML(htmlString) {
			var tag, wrap, j,
				fragment = document.createElement('div');

			// Deserialize a standard representation
			tag = (rtagName.exec(htmlString) || ["", ""])[1].toLowerCase();
			wrap = wrapMap[tag] || wrapMap._default;
			fragment.innerHTML = wrap[1] + htmlString + wrap[2];

			// Descend through wrappers to the right content
			j = wrap[0];
			while (j--) {
				fragment = fragment.lastChild;
			}

			return fragment;
		}

		if (apiResult.widgets) {
			for (var w in apiResult.widgets) {
				var obj = apiResult.widgets[w];
				if (obj && typeof (obj) == "object") {
					if (obj.name == 'title' && obj.content && obj.content != '') {
						document.title = obj.content;
						continue;
					}
					else if (obj.name == 'description' && obj.content && obj.content != '') {
						const md = document.head.querySelector('meta[name="description"]');
						if (md) md.content = obj.content;
						continue;
					}

					var el = obj.name == 'body' ? document.body : document.getElementById(obj.name);

					if (obj.action == 'remove') {
						if (el) el.parentElement.removeChild(el);
						continue;
					}

					const shadowel = shadow.getElementById(obj.name);
					if (shadowel) {
						el = shadowel;
						obj.nested = true;
					}

					var parentel = null;
					if (obj.action == 'adjacent') {
						if (obj.parent && obj.parent != 'body' && obj.parent != '') {
							if (!obj.parent.startsWith(':')) {
								parentel = shadow.getElementById(obj.parent);
								if (parentel) {
									obj.nested = true;
									el = shadow.getElementById(obj.name);
								}
								else
									parentel = document.getElementById(obj.parent);
							}
							else
								obj.selector = true;
						}
						else
							parentel = document.body;
					}

					if (obj.action == 'replace' || (el && obj.action == 'adjacent') || obj.action == 'add') {
						if (!el) continue;
						obj.content = parseHTML(obj.content);
						obj.el = el;
						if (obj.action == 'add')
							obj.func = addFunc;
						else
							obj.func = replaceFunc;
						nodes.push(obj);
					}
					else if (obj.action == 'adjacent') {
						if (!parentel && !obj.selector) continue;
						obj.content = parseHTML(obj.content);
						obj.el = parentel;
						obj.func = adjacentFunc;
						nodes.push(obj);
					}
					else if (obj.action == 'addShadow') {
						if (!el) continue;
						obj.content = parseHTML(obj.content);
						obj.el = el;
						obj.func = addShadowFunc;
						nodes.push(obj);
					}

					if (obj.nested)
						obj.func(el || parentel, obj);
					else {
						const parent = obj.el && obj.el.parentNode.nodeName == 'HEAD' ? shadow.head : shadow.body;
						if (obj.func == replaceFunc)
							replaceid(obj.el, obj);
						parent.appendChild(obj.content);
					}
				}
			}

			const ctrls = processControls(shadow, apiResult.ctrl);
			var focusSet = false;

			nodes.forEach(function (n) {
				if (n.nested) return;
				n.func(n.el, n);

				const ctrl = instance.findControl(n.el);
				if (ctrl) {
					const t = ctrl.root.getAttribute('data-ctrl');
					if (ctrl.instance.widgetContentChanged)
						ctrl.instance.widgetContentChanged();
					else if (window[t] && window[t]['widgetContentChanged']) {
						window[t]['widgetContentChanged'](ctrl.instance);
						console.log('widget: ' + ctrl.id + ' widgetContentChanged ' + t);
					}
				}

				if (!focusSet && n.el) {
					const fel = n.el.querySelector('*[autofocus]');
					if (fel) {
						cu.setFocus(fel);
						focusSet = true;
					}
				}
			});

			postProcessControls(ctrls);
		}

		if (apiResult.redirect) {
			state.loc.url = apiResult.redirect.url;
			state.loc.parms = apiResult.redirect.parms;
			if (apiResult.redirect.isBack)
				state.loc.storage.pop();
			window.history.pushState(state.loc, "", apiResult.redirect.url);
			console.log('push state:', apiResult.redirect.url);
		}

		if (apiResult.arggroups) {
			state.loc.arggroups = apiResult.arggroups;
		}

		const current = document.getElementById(META_CURRENT);
		current.setAttribute('data-href', state.loc.url);

		if (apiResult.clientactions) {
			var ca;
			for (var i = 0; i < apiResult.clientactions.length; i++) {
				ca = apiResult.clientactions[i];
				runClientAction(ca.service, ca.callChain, 0);
			}
		}

		if (apiResult.hardredirect) {
			if (apiResult.hardredirect.url)
				window.location = apiResult.hardredirect.url;
			else
				window.location = window.location;
		}

		if (window.homePage) homePage.countNavBodyHeight();

		console.log("renderApiResult complete");
	}

	

	// ==================== Reactive Helpers ====================

	// Ensure state.ctrl[id] is a reactive object.
	// If already reactive, returns as-is. Otherwise wraps with reactive().
	// Properties that are plain arrays get converted to reactive arrays.
	function ensureReactiveCtrl(id) {
		if (!state.ctrl[id]) {
			state.ctrl[id] = reactive({});
		} else if (!isReactive(state.ctrl[id])) {
			state.ctrl[id] = reactive(state.ctrl[id]);
		}
		return state.ctrl[id];
	}

	// Ensure the ctrl has an effectScope for lifecycle management.
	function ensureEffectScope(ctrlInst) {
		if (!ctrlInst._scope) {
			ctrlInst._scope = effectScope();
		}
		return ctrlInst._scope;
	}

	// ==================== End Reactive Helpers ====================
	function getCtrlState(ctrlName) {
		const c = state.ctrl[ctrlName];
		const cstate = c.state ? c.state : c;
		return cstate;
	}
	function processControls(doc, ctrlCollection) {
		const ctrls = doc.querySelectorAll('[data-ctrl]');
		const bindels = doc.querySelectorAll('[data-hasclientstate]');
		const cntnrels = doc.querySelectorAll('[data-c-type]');
		const newcntnrs = [];
		const deferredRunners = [];

		for (var j = 0; j < cntnrels.length; j++) {
			newcntnrs.push(cntnrels[j].id);
		}

		for (var j = 0; j < bindels.length; j++) {
			const node = bindels[j];
			const st = getElementStateAttrs(node);
			if (!st) continue;

			// skip computed — they are bound after ctrl init via _computedRefs
			if (st.type == 'computed') continue;

			const container = cu.getThisOrParent(node, function (n) { return n.hasAttribute && n.hasAttribute('data-c-type'); });
			const isnewc = container && newcntnrs.indexOf(container.id) >= 0;

			const nodectrl = ensureReactiveCtrl(st.owner);
			const scope = ensureEffectScope(nodectrl);
			const doReplace = ctrlCollection && ctrlCollection[st.owner] &&
				ctrlCollection[st.owner].replaceValues && ctrlCollection[st.owner].replaceValues.indexOf(st.name) >= 0;

			const { owner: ctrlName, name: varName, key: aiKey, index: aiIndex, keyfield: aiKeyField, field: aiField } = st;
			const cstate = getCtrlState(ctrlName);

			if (st.type == 'array') {
				// Initialize property on reactive object
				if (!cstate[varName]) {
					cstate[varName] = [];
				}

				if (isnewc || doReplace) {
					if (node.value) {
						var a = cstate[varName];
						// Populate from DOM value
						const values = node.value.split(',').filter(String);
						for (var i = 0; i < values.length; i++) {
							a.push(values[i]);
						}
					}
				}

				// state → DOM: batched effect reads the reactive array (lazy — deferred until state is populated)
				deferredRunners.push(function () {
					createBatchedEffect(function () {
						const cstate = getCtrlState(ctrlName);
						var a = cstate[varName];

						// Access .length and iterate to track all array mutations
						var joined = a.join(',');
						Tango.setNodeDisplayValue(node, joined, state.ctrl[ctrlName], varName);
					}, scope, false)
				});

				// node → state (twoway)
				if (st.bindtype == 'twoway') {
					node.addEventListener('change', function () {
						const cstate = getCtrlState(ctrlName);
						var a = cstate[varName];
						var newValues = node.value.split(',').filter(String);
						a.splice(0, a.length);
						for (var i = 0; i < newValues.length; i++) {
							a.push(newValues[i]);
						}
					});
				}
			} else if (st.type == 'arrayitem') {
				// state → DOM: effect reads the array element reactively (lazy — deferred until state is populated)
				deferredRunners.push(function () {
					createBatchedEffect(function () {
						const cstate = getCtrlState(ctrlName);
						var a = cstate[varName];

						var idx;
						if (aiKey != null) {
							idx = -1;
							for (var k = 0; k < a.length; k++) {
								if (a[k] && a[k][aiKeyField] === aiKey) { idx = k; break; }
							}
						} else {
							idx = aiIndex;
						}
						if (idx >= 0 && idx < a.length) {
							var val = aiField ? a[idx][aiField] : a[idx];
							Tango.setNodeDisplayValue(node, val, state.ctrl[ctrlName], varName);
						}
					}, scope, false)
				});

				// node → state (twoway)
				if (st.bindtype == 'twoway') {
					node.addEventListener('input', function () {
						const cstate = getCtrlState(ctrlName);
						var a = cstate[varName];

						var idx;
						if (aiKey != null) {
							idx = -1;
							for (var k = 0; k < a.length; k++) {
								if (a[k] && a[k][aiKeyField] === aiKey) { idx = k; break; }
							}
						} else {
							idx = aiIndex;
						}
						if (idx >= 0) {
							if (aiField) {
								a[idx][aiField] = node.value;
							} else {
								a[idx] = node.value;
							}
						}
					});
				}
			} else if (st.type == 'value') {
				// Initialize property on reactive object
				if (!cstate[varName])
					cstate[varName] = undefined;

				if (isnewc || doReplace) {
					if (node.value)
						cstate[varName] = node.value;
				}

				// state → DOM: batched effect (lazy — deferred until state is populated)
				deferredRunners.push(function () {
					createBatchedEffect(function () {
						const cstate = getCtrlState(ctrlName);
						const val = cstate.hasOwnProperty(varName) ? cstate[varName] : undefined;
						if (val != undefined)
							Tango.setNodeDisplayValue(node, val, state.ctrl[ctrlName], varName);
					}, scope, false)
				});

				// node → state (twoway): listen for user edits
				if (st.bindtype == 'twoway') {
					node.addEventListener('input', function () {
						// Write raw user input to reactive state.
						// The effect above will NOT re-run during typing because
						// the value being set is the same as what effect would read.
						// We suppress the effect during input to avoid cursor issues.
						const cstate = getCtrlState(ctrlName);
						cstate[varName] = node.value;
					});
					// Re-format on blur (user finished editing)
					node.addEventListener('blur', function () {
						const cstate = getCtrlState(ctrlName);
						cstate[varName] = node.value;
						// Force re-format via direct DOM write
						Tango.setNodeDisplayValue(node, cstate, state.ctrl[ctrlName], varName);
					});
				}
			}
		}

		

		const assignProps = (ctrlInst, srvInst) => {
			for (var prop in srvInst) {
				if (Object.prototype.hasOwnProperty.call(ctrlInst, prop) && Array.isArray(srvInst[prop]) && Array.isArray(ctrlInst[prop])) {
					// Replace contents of existing reactive array
					var arr = ctrlInst[prop];
					arr.splice(0, arr.length);
					for (var i = 0; i < srvInst[prop].length; i++) {
						arr.push(srvInst[prop][i]);
					}
				} else {
					ctrlInst[prop] = srvInst[prop];
				}
			}
		};

		for (var i = 0; i < ctrls.length; i++) {
			var root = ctrls[i];
			const t = root.getAttribute('data-ctrl');
			const ctrl = instance.findControl(root);
			if (root.id != ctrl.id) root = doc.getElementById(ctrl.id);

			if (!ctrl.instance) {
				ctrl.instance = ensureReactiveCtrl(ctrl.id);
			}

			const srvCtrl = ctrlCollection ? ctrlCollection[ctrl.id] : null;

			if (!ctrl.instance.type) {
				ctrl.instance.type = t
				ctrl.instance.root = ctrl.id;
				if (Tango.serviceProvider.components[t.toLowerCase()]) {
					ctrl.instance = Tango.serviceProvider.components[t.toLowerCase()](ctrl.id, Tango.serviceProvider);
					// Re-wrap as reactive if the factory returned a plain object
					if (!isReactive(ctrl.instance)) {
						state.ctrl[ctrl.id] = reactive(ctrl.instance);
						ctrl.instance = state.ctrl[ctrl.id];
					} else {
						state.ctrl[ctrl.id] = ctrl.instance;
					}
				}

				if (srvCtrl && srvCtrl.props) {
					if (!ctrl.instance.props) ctrl.instance.props = {};
					ctrl.instance.props = Object.assign(ctrl.instance.props, srvCtrl.props);
				}
				if (srvCtrl && srvCtrl.fields) {
					if (!ctrl.instance.fields) ctrl.instance.fields = {};
					ctrl.instance.fields = Object.assign(ctrl.instance.fields, srvCtrl.fields);
				}

				if (ctrl.instance.init)
					ctrl.instance.init();
				else if (window[t] && window[t]['init']) {
					window[t]['init'](root, ctrl.instance);
					console.log('widget: ' + ctrl.id + ' init ' + t);
				}
			}

			if (srvCtrl && srvCtrl.instance) {
				assignProps(ctrl.instance, srvCtrl.instance);
			}

			if (srvCtrl && srvCtrl.state) {
				if (!ctrl.instance.state) ctrl.instance.state = {};
				assignProps(ctrl.instance.state, srvCtrl.state);
			}

			if (ctrl.instance.widgetWillMount)
				ctrl.instance.widgetWillMount(doc);
			else if (window[t] && window[t]['widgetWillMount']) {
				window[t]['widgetWillMount'](doc, ctrl.instance);
				console.log('widget: ' + ctrl.id + ' widgetWillMount ' + t);
			}

			if (srvCtrl)
				delete ctrlCollection[ctrl.id];
		}

		if (ctrlCollection) {
			for (var id in ctrlCollection) {
				const srvCtrl = ctrlCollection[id];
				const inst = state.ctrl[id];
				if (inst) {
					if (srvCtrl && srvCtrl.props) {
						if (!inst.props) inst.props = {};
						inst.props = Object.assign(inst.props, srvCtrl.props);
					}

					if (srvCtrl && srvCtrl.instance) {
						assignProps(inst, srvCtrl.instance);
					}

					if (srvCtrl && srvCtrl.state) {
						if (!inst.state) inst.state = {};
						assignProps(inst.state, srvCtrl.state);
					}
				}
			}
		}

		// Flush deferred binding effects — state is now fully populated
		// (after init, assignProps, widgetWillMount, and computed activation)
		for (var i = 0; i < deferredRunners.length; i++) {
			deferredRunners[i]();
		}

		// Activate computed properties for all controls
		for (var i = 0; i < ctrls.length; i++) {
			var root = ctrls[i];
			var ctrl = instance.findControl(root);
			if (ctrl && ctrl.instance && ctrl.instance._computedDefs) {
				_activateComputeds(ctrl.id);
			}
			if (ctrl && ctrl.instance && ctrl.instance._nestedComputedDefs) {
				_activateNestedComputeds(ctrl.id);
			}
		}

		// Bind computed DOM nodes to their _computedRefs
		var computedEls = doc.querySelectorAll('[data-hasclientstate="computed"]');
		for (var j = 0; j < computedEls.length; j++) {
			const cnode = computedEls[j];
			const cst = getElementStateAttrs(cnode);
			if (!cst) continue;
			const ctrlInst = state.ctrl[cst.owner];
			if (!ctrlInst || !ctrlInst._computedRefs || !ctrlInst._computedRefs[cst.name]) continue;
			const cscope = ensureEffectScope(ctrlInst);
			const varName = cst.name;

			createBatchedEffect(function () {
				const val = ctrlInst._computedRefs[varName];
				Tango.setNodeDisplayValue(cnode, val, ctrlInst, varName);
			}, cscope, false);
		}

		return ctrls;
	}

	// ==================== Computed Activation ====================
	// Activates computed properties defined via Tango.Component.defineComputed().
	// Each def.fn is wrapped in @vue/reactivity computed(), and an effect is created
	// to sync the computed value to bound DOM nodes.
	function _activateComputeds(ctrlId) {
		var ctrlInst = state.ctrl[ctrlId];
		if (!ctrlInst || !ctrlInst._computedDefs) return;

		if (!ctrlInst._computedRefs) ctrlInst._computedRefs = {};
		var scope = ensureEffectScope(ctrlInst);

		for (var propName in ctrlInst._computedDefs) {
			(function (name) {
				if (ctrlInst._computedRefs[name]) return; // already activated

				var def = ctrlInst._computedDefs[name];
				var c = scope.run(function () {
					return computed(def.fn.bind(ctrlInst));
				});
				ctrlInst._computedRefs[name] = c;

				const cstate = getCtrlState(ctrlId);
				// Define getter so that ctrl.propName returns computed value
				// and also participates in Vue's reactive tracking
				Object.defineProperty(cstate, name, {
					enumerable: true,
					configurable: true,
					get: function () {
						return c.value;
					}
				});
			})(propName);
		}
	}

	// ==================== End Computed Activation ====================

	// ==================== Nested Computed Activation ====================
	// Activates nested computed properties defined via the second signature of
	// Tango.Component.defineComputed(targetAccessor, fieldName, fn).
	//
	// Unlike flat computeds (which create a computed ref + getter on ctrl instance),
	// nested computeds create a reactive effect that writes the computed value
	// directly into the target object (e.g. state.Periods[i].Ebitda).
	// This way, existing arrayitem DOM bindings that read state.Periods[i].Ebitda
	// pick up changes automatically through Vue's reactivity system.
	function _activateNestedComputeds(ctrlId) {
		var ctrlInst = state.ctrl[ctrlId];
		if (!ctrlInst || !ctrlInst._nestedComputedDefs) return;

		var scope = ensureEffectScope(ctrlInst);

		for (var i = 0; i < ctrlInst._nestedComputedDefs.length; i++) {
			(function (def) {
				scope.run(function () {
					effect(function () {
						var target = def.targetAccessor(ctrlInst.state);
						if (!target) return;
						var newVal = def.fn.call(ctrlInst, target);
						// Write only if value changed to avoid infinite loops
						// in chains like Revenue → EBITDA → EBIT → NOPAT → UFCF
						if (target[def.field] !== newVal) {
							target[def.field] = newVal;
						}
					});
				});
			})(ctrlInst._nestedComputedDefs[i]);
		}
	}

	// ==================== End Nested Computed Activation ====================

	function postProcessControls(ctrls) {
		for (var i = 0; i < ctrls.length; i++) {
			const root = ctrls[i];
			const ctrl = instance.findControl(root);

			if (ctrl) {
				const t = root.getAttribute('data-ctrl');
				if (ctrl.instance.widgetDidMount)
					ctrl.instance.widgetDidMount();
				else if (window[t] && window[t]['widgetDidMount']) {
					window[t]['widgetDidMount'](ctrl.instance);
					console.log('widget: ' + ctrl.id + ' widgetDidMount ' + t);
				}
			}
		}
	}

	function runOnAjaxSend(el, target) {
		var node = el;
		node.classList.add('ajax-loading');
		do {
			node = cu.getParent(node, function (n) { return n.hasAttribute('data-ctrl'); });
			if (!node) break;
			const t = node.getAttribute('data-ctrl');
			if (window[t] && window[t]['onAjaxSend']) {
				window[t]['onAjaxSend'](el, target, state.ctrl[node.id]);
				console.log('widget: ' + node.id + ' onAjaxSend ' + t);
			}
		} while (true);
	}

	function runClientAction(service, callChain, iter) {
		if (iter > 10) return;
		var caller = window[service];
		if (caller) {
			for (var j = 0; j < callChain.length; j++) {
				step = callChain[j];
				if (step.method == 'apply')
					caller = caller[step.method](caller, Array.isArray(step.args) ? step.args : [step.args]);
				else
					caller = caller[step.method](step.args);
			}
		}
		else {
			console.log("wait for " + service);
			setTimeout(function () { runClientAction(service, callChain, iter++); }, 50);
		}
	}

	function getHistoryState() {
		var s = window.history.state;
		if (!s) {
			s = { storage: [{}] };
		} else {
			s = JSON.parse(JSON.stringify(s));
		}

		if (!s.parms || Object.keys(s.parms).length == 0) {
			s.parms = {};
			s.parms['p'] = document.head.getAttribute('data-page');
			s.url = window.location.pathname + window.location.search;

			const urlParms = cu.getParams(window.location.search.substring(1));
			for (var key in urlParms) {
				if (!s.parms[key]) {
					s.parms[key] = urlParms[key];
				}
			}
		}

		return s;
	}

	document.addEventListener('DOMContentLoaded', function () {
		state.com.message = document.getElementById("topmessagecontainer");
		setTimeout(function () {
			if (state.com.request.default && state.com.message) {
				state.com.message.style.display = 'block';
			}
		}, 100);
		//document.body.className = '';

		window.addEventListener('popstate', function (event) {
			const s = getHistoryState();

			s.parms['c-new'] = 1;
			s.parms['e'] = DEF_EVENT_NAME;
			if (s.parms['r']) delete s.parms['r'];
			if (s.parms['c-prefix']) delete s.parms['c-prefix'];
			if (s.parms['c-type']) delete s.parms['c-type'];

			var parser = document.createElement('a');
			parser.href = state.loc.url;

			if (window.location.pathname != parser.pathname) {
				state.ctrl = {};
				state.loc.storage.pop();
				s.storage = state.loc.storage;
				const curStorage = s.storage.length > 0 ? s.storage[s.storage.length - 1] : {};
				for (var key in curStorage) {
					s.parms[key] = curStorage[key];
				}
			}

			state.loc = s;

			if (s.onBack && !s.parms['c-new'])
				runClientAction(s.onBack.service, s.onBack.callChain, 0);
			else {
				const settings = { url: getApiUrl(s.url), type: 'POST', data: JSON.stringify(s.parms) };
				state.com.onPopState = true;
				ajax(settings).catch(function (err) {
					Tango.processError(err.xhr, err.settings, requestCompleted);
					return Promise.reject(err);
				}).then(onRequestResult).then(processApiResponse);
			}
		});


		if (state.com.apiResult)
			renderApiResult();
	});

	const current = document.getElementById(META_CURRENT);
	state.loc.url = document.location.pathname + document.location.search;
	current.setAttribute('data-href', state.loc.url);
	//instance.runEventFromElementWithApiResponse(current, { method: 'GET', url: state.loc.url, isfirstload: true });
	state.loc.parms['c-new'] = 1;
	//history.replaceState(state.loc, document.title, state.loc.url);

	return instance;
})(commonUtils, reactivity);